// use of snake_case is intentional for analytics event names and data fields as this is what the analytics team desires

import {
	AddDevicesDialogLaunchedEvent,
	AddDevicesDialogCancelledEvent,
	AddDevicesDialogDevicesAddedEvent
} from './add-devices-dialog-event.model';
import { DeviceActionCancelledEvent, DeviceActionEvent } from './device-event.model';
import {
	FirmwareUpdateDialogLaunchedEvent,
	FirmwareUpdateDialogCancelledEvent,
	FirmwareUpdateSubmittedEvent,
	FirmwareUpdateRemovedEvent
} from './firmware-update-event.model';
import { PageViewedEvent } from './general-navigation-event.model';
import {
	ManagedGatewaysDialogLaunchedEvent,
	ManagedGatewaysDialogCancelledEvent,
	ManagedGatewaysDevicesAddedEvent,
	ManagedGatewaysDevicesRemovedEvent,
	ManagedGatewaysDevicesTransferredEvent
} from './managed-gateways-dialog-event.model';
import { ScheduledJobActionEvent, ScheduledJobListRefreshedEvent } from './scheduled-jobs-event.model';
import { TableColumnSettingsChangedEvent, TableExportedEvent } from './table-event.model';

export enum DeviceManagementAnalyticsEvents {
	/* 
	To measure which routes of the CDM app users visit.
	This will be tracked each time a user visits a page.
	Note: the QR Code routing isn't an explicit route in the secondary sidenav. 
	Rather, it will get hit if the user uses their phone's camera to scan a QR code, then follows the link. 
	This is different than if they use the QR Code scanning option of the AddDevices dialog. 
	*/
	PageViewed = 'page_viewed',

	/* 
	To measure when users export data from tables, and in which format.
	This will be tracked each time the user clicks/selects an export option associated with a data table. 
	*/
	TableExported = 'table_exported',

	/* 	 
	To measure which column settings users find most useful.

	When the user navigates to the devices page, the user's table settings that were saved in the user's preferences will be tracked. 
	This info includes which columns are visible, if the default view is the "flat" view or the "connections" view, column sorting, etc. 
	This will help understand which table settings users are most interested in seeing.

	At the moment, user's table settings are only preserved for the devices inventory table.
	*/
	TableColumnSettingsChanged = 'table_column_settings_changed',

	/* 
	To measure how frequenty a user launches the Add Devices dialog
	*/
	AddDevicesDialogLaunched = 'add_devices_dialog_launched',

	/* 
	To measure how frequenty a user cancels out of the Add Devices dialog w/out adding any devices
	*/
	AddDevicesDialogCancelled = 'add_devices_dialog_cancelled',

	/* 
	To measure which method of the AddDevices dialog is used the most.

	This event would get tracked after the user selects one of the methods from Step 1 of the dialog, 
	proceeds through all the steps to add the devices to the BE, then clicks the action to submit the request to the server. 

	To be clear, the event will not be tracked until the user has committed to adding the devices.  
	It won't track the cases where a user opens the dialog, clicks around a little, then cancels out. 
	*/
	AddDevicesDialogDevicesAdded = 'add_devices_dialog_devices_added',

	/*  
	To measure which per-device settings change operations users perform.
	*/
	DeviceAction = 'device_action',

	/*  
	To measure when a user initiates a settings change for a device, but when presented with a confirmation dialog, 
	cancels instead of proceeding.

	Note that only disruptive, or "no going back" operations present the user with a dialog for the user to confirm before being committed.
	*/
	DeviceActionCancelled = 'device_action_cancelled',

	/* 
	To measure how a user interacts with the FW Update dialog, 
	such as where it is normally launched from and how many devices are selected at the time when the dialog is launched.
	*/
	FirmwareUpdateDialogLaunched = 'firmware_update_dialog_launched',

	/* 
	To measure how often a user closes the FW Update dialog w/out updating firmware
	*/
	FirmwareUpdateDialogCancelled = 'firmware_update_dialog_cancelled',

	/* 
	To measure how often a user initiates a firmware update, immediate or scheduled, and the accompanying device information.
	*/
	FirmwareUpdateSubmitted = 'firmware_update_submitted',

	/* 
	To measure how often devices are removed from a scheduled firmware update, and the accompanying device information.
	*/
	FirmwareUpdateRemoved = 'firmware_update_removed',

	/* 
	To measure a user's behavior for scheduled job creation, updating, and cancelling.
	*/
	ScheduledJobAction = 'scheduled_job_action',

	/* 
	To measure how often a user manually refreshes the list of scheduled jobs.
	*/
	ScheduledJobListRefreshed = 'scheduled_job_list_refreshed',

	/* 
	To measure how frequenty a user launches the managed_gateways dialog
	*/
	ManagedGatewaysDialogLaunched = 'managed_gateways_dialog_launched',

	/* 
	To measure how frequenty a user cancels out of the Managed Gateways dialog w/out performing any operations.
	*/
	ManagedGatewaysDialogCancelled = 'managed_gateways_dialog_cancelled',

	/* 
	To measure how frequently user's add/associate devices with a Gateway device
	*/
	ManagedGatewaysDevicesAdded = 'managed_gateways_devices_added',

	/* 
	To measure how frequently user's dissasociate devices with a Gateway device
	*/
	ManagedGatewaysDevicesRemoved = 'managed_gateways_devices_removed',

	/* 
	To measure how frequently a user transfers a device from one Gateway device to another.
	*/
	DevicesManagedGatewaysTransferred = 'devices_managed_gateways_transferred'
}

export type DeviceManagementAnalyticsEventsDataMap = {
	[DeviceManagementAnalyticsEvents.PageViewed]: PageViewedEvent;
	[DeviceManagementAnalyticsEvents.TableExported]: TableExportedEvent;
	[DeviceManagementAnalyticsEvents.TableColumnSettingsChanged]: TableColumnSettingsChangedEvent;
	[DeviceManagementAnalyticsEvents.AddDevicesDialogLaunched]: AddDevicesDialogLaunchedEvent;
	[DeviceManagementAnalyticsEvents.AddDevicesDialogCancelled]: AddDevicesDialogCancelledEvent;
	[DeviceManagementAnalyticsEvents.AddDevicesDialogDevicesAdded]: AddDevicesDialogDevicesAddedEvent;
	[DeviceManagementAnalyticsEvents.DeviceAction]: DeviceActionEvent;
	[DeviceManagementAnalyticsEvents.DeviceActionCancelled]: DeviceActionCancelledEvent;
	[DeviceManagementAnalyticsEvents.FirmwareUpdateDialogLaunched]: FirmwareUpdateDialogLaunchedEvent;
	[DeviceManagementAnalyticsEvents.FirmwareUpdateDialogCancelled]: FirmwareUpdateDialogCancelledEvent;
	[DeviceManagementAnalyticsEvents.FirmwareUpdateSubmitted]: FirmwareUpdateSubmittedEvent;
	[DeviceManagementAnalyticsEvents.FirmwareUpdateRemoved]: FirmwareUpdateRemovedEvent;
	[DeviceManagementAnalyticsEvents.ScheduledJobAction]: ScheduledJobActionEvent;
	[DeviceManagementAnalyticsEvents.ScheduledJobListRefreshed]: ScheduledJobListRefreshedEvent;
	[DeviceManagementAnalyticsEvents.ManagedGatewaysDialogLaunched]: ManagedGatewaysDialogLaunchedEvent;
	[DeviceManagementAnalyticsEvents.ManagedGatewaysDialogCancelled]: ManagedGatewaysDialogCancelledEvent;
	[DeviceManagementAnalyticsEvents.ManagedGatewaysDevicesAdded]: ManagedGatewaysDevicesAddedEvent;
	[DeviceManagementAnalyticsEvents.ManagedGatewaysDevicesRemoved]: ManagedGatewaysDevicesRemovedEvent;
	[DeviceManagementAnalyticsEvents.DevicesManagedGatewaysTransferred]: ManagedGatewaysDevicesTransferredEvent;
};
